import React, { useState } from 'react';
import CustomHeader from '../components/CustomHeader';
import Footer from '../components/Footer';
import '../styles/screens/Support.css';

const Support: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteData = async () => {
    if (!email) {
      setDeleteError('Please enter your registered email address');
      return;
    }

    if (!email.includes('@')) {
      setDeleteError('Please enter a valid email address');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');
    setDeleteMessage('');

    try {
      const response = await fetch(`https://api.rush-hour.app/api/v1/user?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setDeleteMessage('Your data has been successfully deleted. You will need to sign up again if you wish to use RushHour in the future.');
        setEmail('');
      } else if (response.status === 404) {
        setDeleteError('No account found with this email address. Please check your email and try again.');
      } else {
        setDeleteError('An error occurred while deleting your data. Please try again later or contact support.');
      }
    } catch (error) {
      setDeleteError('Network error. Please check your internet connection and try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="support-container">
      <CustomHeader />

      {/* Main Content */}
      <main className="support-content">
        <div className="container">
          <h1 className="support-title">Support & Help Center</h1>
          <p className="support-date">We're here to help you with any questions or concerns</p>

          <section className="support-section">
            <h2>Contact Us</h2>
            <p>
              Need help with RushHour? Our support team is here to assist you with any questions, 
              technical issues, or general inquiries about our parking assistance app.
            </p>
            <div className="contact-methods">
              <div className="contact-method">
                <h3>Email Support</h3>
                <p>For general inquiries and support:</p>
                <a href="mailto:rush.hour.contact.us@gmail.com" className="contact-email">
                  rush.hour.contact.us@gmail.com
                </a>
                <p className="response-time">Response time: Within 48 hours</p>
              </div>
              
              <div className="contact-method">
                <h3>Technical Support</h3>
                <p>For app-related issues and bugs:</p>
                <a href="mailto:rush.hour.contact.us@gmail.com" className="contact-email">
                  rush.hour.contact.us@gmail.com
                </a>
                <p className="response-time">Response time: Within 48 hours</p>
              </div>
            </div>
          </section>

          <section className="support-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-item">
              <h3>How do I register my vehicle?</h3>
              <p>
                After signing in with Google, go to the Settings and just "Add a Car" 
                to add a new vehicle. Enter your license plate number and the country's car.
              </p>
            </div>
            
            <div className="faq-item">
              <h3>What should I do if someone blocks my car?</h3>
              <p>
                Use the "I need to leave" feature to notify the blocking driver. The app will send 
                them a notification, and you'll be notified when they move their vehicle.
              </p>
            </div>
            
            <div className="faq-item">
              <h3>How do I receive notifications?</h3>
              <p>
                Make sure push notifications are enabled in your device settings and in the app. 
                You'll receive notifications about blocking situations and when blocking cars move.
              </p>
            </div>
            
            <div className="faq-item">
              <h3>Is my personal information secure?</h3>
              <p>
                Yes, we use industry-standard security measures including HTTPS encryption and JWT 
                authentication. Your data is stored securely on servers in Finland (EU).
              </p>
            </div>
          </section>

          <section className="support-section">
            <h2>Data Management</h2>
            <p>
              You have full control over your personal data. You can request deletion of your 
              account and all associated data at any time.
            </p>
            
            <div className="delete-data-section">
              <h3>Delete My Data</h3>
              <p>
                To permanently delete your account and all associated data, please enter your 
                registered email address below. This action cannot be undone.
              </p>
              
              <div className="delete-form">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Your Registered Email:</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="form-input"
                    disabled={isDeleting}
                  />
                </div>
                
                <button
                  onClick={handleDeleteData}
                  disabled={isDeleting}
                  className="delete-button"
                >
                  {isDeleting ? 'Deleting...' : 'Delete My Data'}
                </button>
                
                {deleteError && (
                  <div className="error-message">
                    {deleteError}
                  </div>
                )}
                
                {deleteMessage && (
                  <div className="success-message">
                    {deleteMessage}
                  </div>
                )}
              </div>
              
              <div className="delete-warning">
                <h4>⚠️ Important Information</h4>
                <ul>
                  <li>This action will permanently delete your account and all associated data</li>
                  <li>You will lose access to all app features</li>
                  <li>This action cannot be undone</li>
                  <li>You will need to sign up again if you want to use RushHour in the future</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="support-section">
            <h2>App Information</h2>
            <div className="app-info">
              <div className="info-item">
                <strong>App Version:</strong> 1.0.0
              </div>
              <div className="info-item">
                <strong>Platform:</strong> React Native (iOS & Android)
              </div>
              <div className="info-item">
                <strong>Last Updated:</strong> August 2025
              </div>
              <div className="info-item">
                <strong>Data Storage:</strong> Helsinki, Finland (EU)
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Support;
